import { useRef, useState, useCallback } from "react";
import { toPng } from "html-to-image";
import { Download, Image as ImageIcon } from "lucide-react";
import {
  VoiceWaveform,
  AIInsightCard,
  ModuleFlowDiagram,
  LiveDashboardPulse,
  GPSTrackingVisual,
  OCRScanVisual,
  GanttChartVisual,
  LeaderboardVisual,
  CVExtractionVisual,
  JobCardFlowVisual,
  BadgesRewardsVisual,
} from "../components/VisualElements";

const VISUALS = [
  { key: "leaderboard", title: "Configurable Leaderboard (Analytics Hub)", w: 720, Comp: LeaderboardVisual },
  { key: "gantt", title: "Project Gantt (Solar Array B)", w: 920, Comp: GanttChartVisual },
  { key: "badges", title: "Teams · Badges & Recognition", w: 720, Comp: BadgesRewardsVisual },
  { key: "cv-extraction", title: "Recruitment · CV Extraction & Role Match", w: 820, Comp: CVExtractionVisual },
  { key: "jobcard-flow", title: "Ops · Job Card Completion Flow", w: 620, Comp: JobCardFlowVisual },
  { key: "gps-tracking", title: "Ops · Live Field-Team GPS Tracking", w: 620, Comp: GPSTrackingVisual },
  { key: "ocr-scan", title: "Stockroom · Invoice OCR Scan", w: 720, Comp: OCRScanVisual },
  { key: "voice-waveform", title: "Cerebrum · Voice → Transcription", w: 720, Comp: VoiceWaveform },
  { key: "ai-insight", title: "Cerebrum · Cross-Module AI Insights", w: 620, Comp: AIInsightCard },
  { key: "module-flow", title: "Data Flow Across Modules", w: 720, Comp: ModuleFlowDiagram },
  { key: "live-pulse", title: "Live Platform Pulse", w: 620, Comp: LiveDashboardPulse },
];

function RenderTile({ title, width, Comp, idx }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(2);

  const handleDownload = useCallback(async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(ref.current, {
        cacheBust: true,
        pixelRatio: scale,
        backgroundColor: "#0a0b0e",
      });
      const link = document.createElement("a");
      link.download = `geminii-render-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Download failed:", e);
      alert("Couldn't generate PNG — see console.");
    } finally {
      setBusy(false);
    }
  }, [title, scale]);

  return (
    <section
      data-testid={`render-tile-${idx}`}
      className="border border-white/[0.06] rounded-2xl bg-white/[0.01] p-6 md:p-8"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <span className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider block mb-1">
            {String(idx + 1).padStart(2, "0")} / {VISUALS.length}
          </span>
          <h3 className="font-display font-bold text-base text-white">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 p-1 bg-white/[0.03] rounded-lg border border-white/[0.05]">
            {[1, 2, 3].map((s) => (
              <button
                key={s}
                onClick={() => setScale(s)}
                className={`text-[10px] font-mono px-2 py-1 rounded transition-colors ${
                  scale === s ? "bg-rogue-cyan/15 text-rogue-cyan" : "text-geminii-dim hover:text-white"
                }`}
              >
                {s}x
              </button>
            ))}
          </div>
          <button
            onClick={handleDownload}
            disabled={busy}
            data-testid={`render-download-${idx}`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rogue-cyan text-geminii-deep text-xs font-semibold hover:shadow-[0_0_18px_rgba(31,182,193,0.35)] disabled:opacity-60 transition-all"
          >
            {busy ? "Rendering…" : (<><Download size={14} /> PNG</>)}
          </button>
        </div>
      </header>

      {/* Capture target — visual rendered at fixed width for predictable export */}
      <div className="flex justify-center">
        <div ref={ref} style={{ width, maxWidth: "100%" }}>
          <Comp />
        </div>
      </div>
    </section>
  );
}

export default function RendersPage() {
  const downloadAll = useCallback(async () => {
    const tiles = document.querySelectorAll("[data-testid^='render-download-']");
    for (const btn of tiles) {
      btn.click();
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 1200));
    }
  }, []);

  return (
    <div className="min-h-screen pt-32 md:pt-40 pb-20" data-testid="renders-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon size={16} className="text-rogue-cyan" />
            <span className="text-[10px] font-mono text-rogue-cyan uppercase tracking-wider">
              Internal · Render Gallery
            </span>
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3">
            Visual renders for the Rogue app team
          </h1>
          <p className="text-geminii-muted max-w-2xl mb-6">
            Every animated marketing visual on this site, exportable as a high-resolution PNG.
            Use these as reference when aligning the actual Rogue app UI to the brand language.
            Each tile renders at <span className="text-white">1×, 2×, or 3×</span> pixel density.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={downloadAll}
              data-testid="renders-download-all"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-rogue-cyan text-geminii-deep text-sm font-semibold hover:shadow-[0_0_24px_rgba(31,182,193,0.35)] transition-all"
            >
              <Download size={16} />
              Download all ({VISUALS.length})
            </button>
            <span className="text-xs text-geminii-dim">
              Sequentially downloads each PNG at the currently-selected scale per tile.
            </span>
          </div>
        </div>

        {/* Tiles */}
        <div className="space-y-8">
          {VISUALS.map((v, i) => (
            <RenderTile key={v.key} title={v.title} width={v.w} Comp={v.Comp} idx={i} />
          ))}
        </div>

        <p className="text-center text-xs text-geminii-dim mt-16">
          Not linked from the public nav. Share <span className="text-white font-mono">/renders</span> only with the Rogue build team.
        </p>
      </div>
    </div>
  );
}
